/**
 * Dynamic-block grip parser — DYN-4 Phase 4b / Fix 7.
 *
 * Decodes AutoCAD 2018+ grip OBJECTS records (BLOCKLINEARGRIP /
 * BLOCKVISIBILITYGRIP / BLOCKFLIPGRIP / BLOCKROTATIONGRIP /
 * BLOCKALIGNMENTGRIP / BLOCKPROPERTIESTABLEGRIP and their ACDB-prefixed
 * twins) into typed `DynamicGrip` records.
 *
 * Each grip record has a well-defined envelope:
 *
 *     0  BLOCK<KIND>GRIP
 *     5  <handle>                         ← grip's own handle
 *   330  <owner>                          ← evaluation graph owner
 *   100  AcDbEvalExpr
 *    90  <eval node id>                   ← ignored
 *    98  33
 *    99  <version>
 *   100  AcDbBlockElement
 *   300  <display name>                   ← ignored (Russian "Ручка")
 *    98  33
 *    99  <version>
 *  1071  0
 *   100  AcDbBlockGrip                    ← marker
 *    91  <update x id>                    ← eval-node ref
 *    92  <update y id>                    ← eval-node ref
 *  1010  <x>  1020  <y>  1030  <z>        ← position (block space)
 *   280  <flags>
 *    93  <−1>
 *   100  AcDbBlock<Kind>Grip              ← subclass marker
 *     …                                    ← kind-specific payload
 *
 * Some grip variants (e.g. BLOCKPROPERTIESTABLEGRIP) omit the final
 * subclass marker; the leading `AcDbBlockGrip` marker is all the parser
 * relies on. Coordinates are optional — if the grip defers to a paired
 * `BLOCKGRIPLOCATIONCOMPONENT` expression, the 1010/1020 codes are
 * absent and we leave `position` undefined.
 *
 * IMPORTANT — backing-parameter resolution:
 *   The first `330` at the top of the record points at the evaluation
 *   graph owner, NOT the backing parameter. Some grip variants bind to
 *   a parameter via an extra 330 later in the record, AFTER the
 *   AcDbBlockGrip subclass marker. We search from that marker forward
 *   and take the first 330 we find (same heuristic DYN-4 Phase 2 uses
 *   for BLOCKUSERPARAMETER's ACDBASSOCVARIABLE reference).
 *
 * Paired with:
 *   - `dxfBridge.dynamic.writer.grips.ts` — symmetric re-emitter.
 *   - `dynamicBlockGrips.ts`              — Zod schema + type.
 */

import type { DxfDynamicBlockObject, DxfGroup } from "@dxf-ai-editor/dxf-io";

import type { DynamicGrip } from "./dynamicBlockGrips";
import { firstNumber } from "./dxfBridge.dynamic.shared";

// ═══════════════════════════════════════════════════════════════════════════
// Class-name → kind dispatch
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Every AutoCAD spelling we recognise as a grip. Keys are the
 * CANONICAL-prefixed class names (see `canonicalDynamicBlockClass` in
 * dxfBridge.dynamic.shared.ts) so dispatch can stay on a single
 * normalised form. AutoCAD 2018+ un-prefixed variants are already
 * canonicalised by the caller (`buildDynamicBlockMetadata`) before
 * reaching `parseGrip`.
 */
const GRIP_KIND_BY_CLASS: Record<string, DynamicGrip["kind"]> = {
  ACDBBLOCKLINEARGRIP: "linear",
  ACDBBLOCKVISIBILITYGRIP: "visibility",
  ACDBBLOCKFLIPGRIP: "flip",
  ACDBBLOCKROTATIONGRIP: "rotation",
  ACDBBLOCKALIGNMENTGRIP: "alignment",
  ACDBBLOCKPROPERTIESTABLEGRIP: "properties-table",
  // Hypothetical — reserved for future AutoCAD output that exposes a
  // user parameter grip directly rather than via a Properties Palette
  // entry. Not observed in АБИ1.dxf; kept so the enum stays closed.
  ACDBBLOCKUSERPARAMETERGRIP: "user",
};

/**
 * Class-name set mirroring `GRIP_KIND_BY_CLASS` for callers that only
 * need the "is this a grip class?" predicate. Exported so the metadata
 * builder can short-circuit before the table / parameter / action
 * dispatchers run.
 */
export const GRIP_CLASSES: ReadonlySet<string> = new Set(
  Object.keys(GRIP_KIND_BY_CLASS),
);

/**
 * Return `true` when the given (already-canonicalised) dynamic-block
 * class string identifies a grip OBJECT.
 */
export function isGripClass(canonicalType: string): boolean {
  return GRIP_CLASSES.has(canonicalType);
}

/**
 * Fallback kind used when the class name is registered in
 * `GRIP_CLASSES` but not present in `GRIP_KIND_BY_CLASS` (impossible by
 * construction, but keeps the compiler and the runtime consistent).
 */
function resolveKind(canonicalType: string): DynamicGrip["kind"] {
  return GRIP_KIND_BY_CLASS[canonicalType] ?? "linear";
}

// ═══════════════════════════════════════════════════════════════════════════
// Backing-parameter handle extraction
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Locate the first `330` group that appears AFTER the `AcDbBlockGrip`
 * subclass marker. That 330 — when present — is the grip's backing
 * parameter handle. Returns the empty string when the marker is absent
 * (non-standard record) or no 330 follows it (e.g. the properties-table
 * grip, which binds to the BlockPropertiesTable itself).
 */
export function findBackingParameterHandle(rawGroups: DxfGroup[]): string {
  let pastMarker = false;
  for (const g of rawGroups) {
    if (g.code === 100 && g.value === "AcDbBlockGrip") {
      pastMarker = true;
      continue;
    }
    if (pastMarker && g.code === 330) {
      const v = g.value.trim();
      if (v.length > 0) return v;
    }
  }
  return "";
}

// ═══════════════════════════════════════════════════════════════════════════
// Top-level entry point
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Decode one dynamic-block grip OBJECT into a typed `DynamicGrip`.
 *
 * @param obj            The raw dynamic-block object. Caller must have
 *                       confirmed the class via `isGripClass`.
 * @param canonicalType  Canonical (prefixed) class string. Passed in so
 *                       the caller avoids re-running
 *                       `canonicalDynamicBlockClass` on the hot path.
 */
export function parseGrip(
  obj: DxfDynamicBlockObject,
  canonicalType: string,
): DynamicGrip {
  const kind = resolveKind(canonicalType);
  const parameterHandle = findBackingParameterHandle(obj.rawGroups);

  // Position: codes 1010 / 1020 / 1030 (x/y/z in block-definition space).
  // Both x and y must be present to treat the coordinates as trustworthy;
  // z defaults to 0 when only 1010/1020 are emitted (legal in 2D blocks).
  const posX = firstNumber(obj.rawGroups, 1010);
  const posY = firstNumber(obj.rawGroups, 1020);
  const position =
    posX !== undefined && posY !== undefined
      ? {
          x: posX,
          y: posY,
          z: firstNumber(obj.rawGroups, 1030) ?? 0,
        }
      : undefined;

  const grip: DynamicGrip = {
    id: obj.handle,
    kind,
    ownerHandle: obj.ownerHandle ?? "",
    parameterHandle,
    // Preserve the full DXF group list so the writer can fall back to
    // verbatim passthrough (fidelity > reconstruction for Phase 4b).
    rawGroups: obj.rawGroups.slice(),
    ...(position !== undefined ? { position } : {}),
  };
  return grip;
}
